import { collection, doc, getDocs, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

export type DonationCampaign = {
  id: string;
  title: string;
  summary: string;
  goalAmount: number;
  raisedAmount: number;
  isPublished: boolean;
  showOnHome: boolean;
  sortOrder: number;
  status: string;
  liveDashboardEnabled?: boolean;
  liveDashboardSlug?: string;
  liveDashboardTitle?: string;
};

function money(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function mapCampaign(id: string, data: Record<string, unknown>): DonationCampaign {
  return {
    id,
    title: String(data.title ?? "Donation Campaign"),
    summary: String(data.summary ?? ""),
    goalAmount: money(data.goalAmount),
    raisedAmount: money(data.raisedAmount),
    isPublished: data.isPublished === true,
    showOnHome: data.showOnHome === true,
    sortOrder: Number(data.sortOrder ?? 0),
    status: String(data.status ?? "active"),
    liveDashboardEnabled: data.liveDashboardEnabled === true,
    liveDashboardSlug: String(data.liveDashboardSlug ?? ""),
    liveDashboardTitle: String(data.liveDashboardTitle ?? ""),
  };
}

export async function getDonationCampaigns(options: { featuredOnly?: boolean; limitCount?: number } = {}) {
  if (!db) return [];

  try {
    const snap = await getDocs(collection(db, "publicDonationCampaigns"));
    const items = snap.docs
      .map((item) => mapCampaign(item.id, item.data()))
      .filter((item) => item.isPublished && item.status !== "closed" && item.status !== "archived")
      .filter((item) => !options.featuredOnly || item.showOnHome)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
    return typeof options.limitCount === "number" ? items.slice(0, options.limitCount) : items;
  } catch (error) {
    console.warn("[public-web] Failed to load donation campaigns", error);
    return [];
  }
}

export function subscribeDonationCampaign(
  campaignId: string,
  onChange: (campaign: DonationCampaign | null) => void,
  onError?: (error: unknown) => void,
) {
  if (!db || !campaignId) {
    onChange(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, "publicDonationCampaigns", campaignId),
    (snap) => {
      if (!snap.exists()) {
        onChange(null);
        return;
      }
      const campaign = mapCampaign(snap.id, snap.data());
      const visibleOnPublicDashboard = campaign.isPublished || campaign.liveDashboardEnabled;
      onChange(visibleOnPublicDashboard && campaign.status !== "closed" && campaign.status !== "archived" ? campaign : null);
    },
    (error) => {
      console.warn("[public-web] Failed to subscribe to donation campaign", campaignId, error);
      onError?.(error);
      onChange(null);
    },
  );
}

export function subscribeDonationCampaignByDashboardSlug(
  dashboardSlug: string,
  onChange: (campaign: DonationCampaign | null) => void,
  onError?: (error: unknown) => void,
) {
  if (!db || !dashboardSlug) {
    onChange(null);
    return () => {};
  }

  const campaignQuery = query(
    collection(db, "publicDonationCampaigns"),
    where("liveDashboardSlug", "==", dashboardSlug),
    limit(1),
  );

  return onSnapshot(
    campaignQuery,
    (snap) => {
      const first = snap.docs[0];
      if (!first) {
        onChange(null);
        return;
      }
      const campaign = mapCampaign(first.id, first.data());
      const visibleOnPublicDashboard = campaign.isPublished || campaign.liveDashboardEnabled;
      onChange(visibleOnPublicDashboard && campaign.status !== "closed" && campaign.status !== "archived" ? campaign : null);
    },
    (error) => {
      console.warn("[public-web] Failed to subscribe to donation campaign dashboard", dashboardSlug, error);
      onError?.(error);
      onChange(null);
    },
  );
}
